import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import './ProductDetail.css';

const StringDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fromCategoryTitle = location.state?.fromCategoryTitle || 'String Type';

    const [string, setString] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const thumbnailRef = useRef(null);

    useEffect(() => {
        const fetchString = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/strings/${id}`);
                if (!res.ok) { navigate('/products'); return; }
                setString(await res.json());
            } catch {
                navigate('/products');
            } finally {
                setLoading(false);
            }
        };
        fetchString();
    }, [id, navigate]);

    const goBack = () => navigate('/products', { state: { restoreCategory: 'strings' } });

    if (loading) return <div className="loading-page">Loading...</div>;
    if (!string) return null;

    const specs = [
        { label: 'Type',           value: string.type },
        { label: 'Gauge',          value: string.gauge },
        { label: 'Core Material',  value: string.coreMaterial },
        { label: 'Outer Material', value: string.outerMaterial },
        { label: 'Coating',        value: string.coating },
    ].filter(s => s.value && s.value !== 'No' && s.value !== '');

    return (
        <div className="product-detail-page">
            <div className="container">
                <nav className="breadcrumb">
                    <span onClick={() => navigate('/')} className="breadcrumb-link">Home</span>
                    <span className="breadcrumb-separator">›</span>
                    <span onClick={goBack} className="breadcrumb-link">{fromCategoryTitle}</span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="breadcrumb-current">{string.name}</span>
                </nav>

                <div className="product-detail-content">
                    {/* Left — images */}
                    <div className="product-images">
                        {(() => {
                            const allImages = string.images?.length > 0 ? string.images : (string.image ? [string.image] : []);
                            const safeIndex = Math.min(selectedImage, allImages.length - 1);
                            return (
                                <>
                                    <div className="main-image">
                                        {allImages.length > 0 ? (
                                            <img
                                                src={allImages[safeIndex]}
                                                alt={string.name}
                                                className="main-product-img"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <div className="image-placeholder-large">
                                                <span className="category-label">STRING</span>
                                                <div className="image-text">{string.brand}</div>
                                            </div>
                                        )}
                                        {allImages.length > 1 && (
                                            <>
                                                <button className="img-nav-btn img-nav-prev" onClick={() => setSelectedImage(i => Math.max(0, i - 1))} disabled={safeIndex === 0} aria-label="Previous">‹</button>
                                                <button className="img-nav-btn img-nav-next" onClick={() => setSelectedImage(i => Math.min(allImages.length - 1, i + 1))} disabled={safeIndex === allImages.length - 1} aria-label="Next">›</button>
                                                <div className="img-nav-counter">{safeIndex + 1} / {allImages.length}</div>
                                            </>
                                        )}
                                    </div>
                                    {allImages.length > 1 && (
                                        <div className="thumbnail-strip">
                                            {allImages.length > 3 && <button className="thumb-nav-btn" onClick={() => thumbnailRef.current?.scrollBy({ left: -96, behavior: 'smooth' })}>‹</button>}
                                            <div className="thumbnail-images" ref={thumbnailRef}>
                                                {allImages.map((img, i) => (
                                                    <div key={i} className={`thumbnail${safeIndex === i ? ' active' : ''}`} onClick={() => setSelectedImage(i)}>
                                                        <img src={img} alt={`View ${i + 1}`} onError={(e) => { e.target.style.display = 'none'; }} />
                                                    </div>
                                                ))}
                                            </div>
                                            {allImages.length > 3 && <button className="thumb-nav-btn" onClick={() => thumbnailRef.current?.scrollBy({ left: 96, behavior: 'smooth' })}>›</button>}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>

                    {/* Right — info */}
                    <div className="product-info">
                        <h1 className="product-title">{string.name}</h1>
                        <div className="product-brand">Brand: {string.brand}</div>

                        <div className="product-price">
                            <span className="price">$ {string.price}</span>
                            <span className="stock-status in-stock">Available</span>
                        </div>

                        {/* Spec panel */}
                        {specs.length > 0 && (
                            <div className="racquet-info-panel">
                                <h3>Specifications</h3>
                                {specs.map(s => (
                                    <div key={s.label} className="racquet-info-row">
                                        <span className="racquet-info-label">{s.label}</span>
                                        <span className="racquet-info-value">{s.value}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {string.description && (
                            <div className="product-description">
                                <h3>Description</h3>
                                <p>{string.description}</p>
                            </div>
                        )}

                        <div className="action-buttons">
                            <button className="contact-btn" onClick={() => navigate('/contact')}>
                                Contact for Purchase
                            </button>
                            <button className="back-btn" onClick={goBack}>
                                ← Back to Strings
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StringDetail;
